import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Alert,
  Snackbar,
  Menu,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Checkbox,
  Toolbar,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Add,
  Delete,
  MoreVert,
  Search,
  CloudUpload,
  Download,
  Visibility,
  Description,
  PictureAsPdf,
  Image,
  Article,
  Gavel,
  Receipt,
  DeleteOutline,
  GetApp,
  InsertDriveFile,
} from '@mui/icons-material';
import api from '../services/api';

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const perPage = 20;

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    file: null,
    name: '',
    description: '',
    document_type: 'other',
    estate_sale_id: '',
    client_id: '',
  });
  const [formErrors, setFormErrors] = useState({});

  // Sales and clients data for dropdowns
  const [sales, setSales] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetchDocuments();
    fetchSales();
    fetchClients();
  }, [page, typeFilter]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: perPage,
      };

      if (typeFilter !== 'all') {
        params.type = typeFilter;
      }

      const response = await api.get('/documents/', { params });
      const data = response.data;

      setDocuments(data?.documents || []);
      setTotalDocuments(data?.total || 0);
      setTotalPages(data?.pages || 1);
    } catch (error) {
      console.error('Error fetching documents:', error);
      showSnackbar('Failed to fetch documents', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSales = async () => {
    try {
      const response = await api.get('/sales/', { params: { per_page: 100 } });
      setSales(response.data?.sales || response.data || []);
    } catch (error) {
      console.error('Error fetching sales:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients/', { params: { per_page: 100 } });
      setClients(response.data?.clients || response.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleMenuOpen = (event, document) => {
    setAnchorEl(event.currentTarget);
    setSelectedDocument(document);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenModal = () => {
    setFormData({
      file: null,
      name: '',
      description: '',
      document_type: 'other',
      estate_sale_id: '',
      client_id: '',
    });
    setFormErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData({
      file: null,
      name: '',
      description: '',
      document_type: 'other',
      estate_sale_id: '',
      client_id: '',
    });
    setFormErrors({});
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        file,
        name: formData.name || file.name.split('.')[0]
      });
      if (formErrors.file) {
        setFormErrors({ ...formErrors, file: '' });
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.file) errors.file = 'File is required';
    if (!formData.name?.trim()) errors.name = 'Document name is required';
    if (!formData.document_type) errors.document_type = 'Document type is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      const submitData = new FormData();
      submitData.append('file', formData.file);
      submitData.append('name', formData.name);
      submitData.append('document_type', formData.document_type);
      if (formData.description) submitData.append('description', formData.description);
      if (formData.estate_sale_id) submitData.append('estate_sale_id', formData.estate_sale_id);
      if (formData.client_id) submitData.append('client_id', formData.client_id);

      await api.post('/documents/', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      showSnackbar('Document uploaded successfully', 'success');
      handleCloseModal();
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to upload document';
      showSnackbar(errorMsg, 'error');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleOpenDeleteDialog = (document) => {
    setSelectedDocument(document);
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedDocument(null);
  };

  const handleDelete = async () => {
    if (!selectedDocument) return;

    try {
      await api.delete(`/documents/${selectedDocument.id}`);
      showSnackbar('Document deleted successfully', 'success');
      handleCloseDeleteDialog();
      fetchDocuments();
      setSelectedDocuments(selectedDocuments.filter(id => id !== selectedDocument.id));
    } catch (error) {
      console.error('Error deleting document:', error);
      showSnackbar('Failed to delete document', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDocuments.length === 0) return;

    try {
      await Promise.all(
        selectedDocuments.map(docId => api.delete(`/documents/${docId}`))
      );
      showSnackbar(`${selectedDocuments.length} documents deleted successfully`, 'success');
      setSelectedDocuments([]);
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting documents:', error);
      showSnackbar('Failed to delete some documents', 'error');
    }
  };

  const handleDownload = async (document) => {
    try {
      const response = await api.get(`/documents/${document.id}?download=true`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', document.filename || document.name);
      window.document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSnackbar('Download started', 'success');
      handleMenuClose();
    } catch (error) {
      console.error('Error downloading document:', error);
      showSnackbar('Failed to download document', 'error');
    }
  };

  const handlePreview = (document) => {
    setSelectedDocument(document);
    setOpenPreviewDialog(true);
    handleMenuClose();
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getDocumentIcon = (type, mimeType) => {
    if (mimeType?.startsWith('image/')) return <Image color="primary" />;
    if (mimeType === 'application/pdf') return <PictureAsPdf color="error" />;

    switch (type) {
      case 'contract':
        return <Gavel color="primary" />;
      case 'receipt':
        return <Receipt color="success" />;
      case 'photo':
        return <Image color="primary" />;
      default:
        return <InsertDriveFile color="action" />;
    }
  };

  const getDocumentTypeColor = (type) => {
    const colors = {
      contract: 'primary',
      receipt: 'success',
      tax_form: 'warning',
      inventory_list: 'info',
      photo: 'secondary',
      other: 'default',
    };
    return colors[type] || 'default';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredDocuments = documents.filter(doc =>
    doc.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: '' });
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedDocuments(filteredDocuments.map(doc => doc.id));
    } else {
      setSelectedDocuments([]);
    }
  };

  const handleSelectDocument = (documentId) => {
    setSelectedDocuments(prev => {
      if (prev.includes(documentId)) {
        return prev.filter(id => id !== documentId);
      } else {
        return [...prev, documentId];
      }
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4" fontWeight="bold">
            Documents & Contracts
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage documents, contracts, and files for estate sales
          </Typography>
        </div>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpenModal}>
          Upload Document
        </Button>
      </Box>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All Documents" />
          <Tab label="Contracts" />
        </Tabs>
      </Card>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search documents by name or description..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Document Type"
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="contract">Contract</MenuItem>
                  <MenuItem value="receipt">Receipt</MenuItem>
                  <MenuItem value="tax_form">Tax Form</MenuItem>
                  <MenuItem value="inventory_list">Inventory List</MenuItem>
                  <MenuItem value="photo">Photo</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {selectedDocuments.length > 0 && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={handleBulkDelete}
                >
                  Delete {selectedDocuments.length} Selected
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <Box mb={2}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredDocuments.length} of {totalDocuments} documents
        </Typography>
      </Box>

      {/* Documents Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedDocuments.length > 0 && selectedDocuments.length < filteredDocuments.length}
                    checked={filteredDocuments.length > 0 && selectedDocuments.length === filteredDocuments.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Document</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Associated With</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Upload Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDocuments.length > 0 ? (
                filteredDocuments.map((doc) => (
                  <TableRow key={doc.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedDocuments.includes(doc.id)}
                        onChange={() => handleSelectDocument(doc.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getDocumentIcon(doc.document_type, doc.mime_type)}
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {doc.name}
                          </Typography>
                          {doc.description && (
                            <Typography variant="caption" color="text.secondary">
                              {doc.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={doc.document_type?.replace('_', ' ')}
                        color={getDocumentTypeColor(doc.document_type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {doc.estate_sale_id ? `Sale #${doc.estate_sale_id}` : ''}
                        {doc.estate_sale_id && doc.client_id ? ', ' : ''}
                        {doc.client_id ? `Client #${doc.client_id}` : ''}
                        {!doc.estate_sale_id && !doc.client_id ? '-' : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatFileSize(doc.file_size)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, doc)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {searchTerm || typeFilter !== 'all' ? 'No documents found' : 'No documents yet'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                      {searchTerm || typeFilter !== 'all'
                        ? 'Try adjusting your filters or search criteria'
                        : 'Upload your first document to get started'}
                    </Typography>
                    {!searchTerm && typeFilter === 'all' && (
                      <Button variant="contained" startIcon={<Add />} onClick={handleOpenModal}>
                        Upload First Document
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedDocument && handlePreview(selectedDocument)}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>Preview</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedDocument && handleDownload(selectedDocument)}>
          <ListItemIcon>
            <Download fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleOpenDeleteDialog(selectedDocument)}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Upload Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUpload />}
                  sx={{ py: 2, mb: 1 }}
                >
                  Choose File
                  <input
                    type="file"
                    hidden
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx,.txt"
                  />
                </Button>
                {formData.file && (
                  <Typography variant="body2" color="text.secondary">
                    Selected: {formData.file.name} ({formatFileSize(formData.file.size)})
                  </Typography>
                )}
                {formErrors.file && (
                  <Typography variant="caption" color="error">
                    {formErrors.file}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Document Name"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required error={!!formErrors.document_type}>
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    value={formData.document_type}
                    label="Document Type"
                    onChange={(e) => handleFormChange('document_type', e.target.value)}
                  >
                    <MenuItem value="contract">Contract</MenuItem>
                    <MenuItem value="receipt">Receipt</MenuItem>
                    <MenuItem value="tax_form">Tax Form</MenuItem>
                    <MenuItem value="inventory_list">Inventory List</MenuItem>
                    <MenuItem value="photo">Photo</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Associated Sale (Optional)</InputLabel>
                  <Select
                    value={formData.estate_sale_id}
                    label="Associated Sale (Optional)"
                    onChange={(e) => handleFormChange('estate_sale_id', e.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    {sales.map((sale) => (
                      <MenuItem key={sale.id} value={sale.id}>
                        {sale.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Associated Client (Optional)</InputLabel>
                  <Select
                    value={formData.client_id}
                    label="Associated Client (Optional)"
                    onChange={(e) => handleFormChange('client_id', e.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    {clients.map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.name || `Client #${client.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {uploading && (
                <Grid item xs={12}>
                  <Box sx={{ width: '100%' }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Uploading... {uploadProgress}%
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={uploading}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedDocument?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={openPreviewDialog} onClose={() => setOpenPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Document Preview
          <IconButton
            onClick={() => selectedDocument && handleDownload(selectedDocument)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Download />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Box>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                  <Typography variant="body1">{selectedDocument.name}</Typography>
                </Grid>
                {selectedDocument.description && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                    <Typography variant="body1">{selectedDocument.description}</Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                  <Chip
                    label={selectedDocument.document_type?.replace('_', ' ')}
                    color={getDocumentTypeColor(selectedDocument.document_type)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Size</Typography>
                  <Typography variant="body1">{formatFileSize(selectedDocument.file_size)}</Typography>
                </Grid>
              </Grid>

              {selectedDocument.mime_type?.startsWith('image/') ? (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <img
                    src={`/api/documents/${selectedDocument.id}?download=true`}
                    alt={selectedDocument.name}
                    style={{ maxWidth: '100%', maxHeight: '500px' }}
                  />
                </Box>
              ) : (
                <Alert severity="info">
                  Preview not available for this file type. Please download to view.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPreviewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Documents;
